import { supabase } from '../lib/supabase';

// Types for club system
export interface Club {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  created_by?: string;
  status: 'active' | 'inactive' | 'suspended';
  settings: Record<string, any>;
}

export interface ClubMember {
  id: number;
  club_id: number;
  player_id: number;
  role: 'owner' | 'admin' | 'organizer' | 'member';
  joined_at: string;
  status: 'active' | 'inactive' | 'banned';
  invited_by?: number;
}

export interface ClubWithDetails extends Club {
  created_by_name?: string;
  member_count: number;
  owner_count: number;
  admin_count: number;
  organizer_count: number;
  regular_member_count: number;
}

export interface ClubMemberWithDetails extends ClubMember {
  player_name: string;
  player_email: string;
  invited_by_name?: string;
}

export interface UserClubMembership {
  membership_id: number;
  club_id: number;
  club_name: string;
  club_description?: string;
  role: 'owner' | 'admin' | 'organizer' | 'member';
  joined_at: string;
  membership_status: 'active' | 'inactive' | 'banned';
  is_owner: boolean;
  is_admin: boolean;
  is_organizer: boolean;
}

// Club Service Functions
export const getClubs = async (): Promise<ClubWithDetails[]> => {
  const { data, error } = await supabase
    .from('club_details')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching clubs:', error);
    throw error;
  }

  return data || [];
};

export const getClub = async (id: number): Promise<ClubWithDetails> => {
  const { data, error } = await supabase
    .from('club_details')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching club:', error);
    throw error;
  }

  return data;
};

export const getUserClubMemberships = async (): Promise<UserClubMembership[]> => {
  const { data, error } = await supabase
    .from('user_club_memberships')
    .select('*')
    .order('club_name');

  if (error) {
    console.error('Error fetching user club memberships:', error);
    throw error;
  }

  return data || [];
};

export const getUserManageableClubs = async (): Promise<ClubWithDetails[]> => {
  const { data, error } = await supabase
    .rpc('get_user_manageable_clubs', { p_user_uuid: (await supabase.auth.getUser()).data.user?.id });

  if (error) {
    console.error('Error fetching manageable clubs:', error);
    throw error;
  }

  return data || [];
};

export const createClub = async (clubData: {
  name: string;
  description?: string;
}): Promise<Club> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('clubs')
    .insert({
      name: clubData.name,
      description: clubData.description,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating club:', error);
    throw error;
  }

  // Get the player ID for the current user
  const { data: player } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!player) {
    throw new Error('Player profile not found');
  }

  // Add the creator as the owner
  await addClubMember(data.id, player.id, 'owner');

  return data;
};

export const updateClub = async (id: number, updates: Partial<Club>): Promise<Club> => {
  const { data, error } = await supabase
    .from('clubs')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating club:', error);
    throw error;
  }

  return data;
};

export const deleteClub = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('clubs')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting club:', error);
    throw error;
  }
};

export const getClubMembers = async (clubId: number): Promise<ClubMemberWithDetails[]> => {
  const { data, error } = await supabase
    .rpc('get_club_members', { p_club_id: clubId });

  if (error) {
    console.error('Error fetching club members:', error);
    throw error;
  }

  return data || [];
};

export const getPlayersFromUserClubs = async (): Promise<any[]> => {
  const { data, error } = await supabase
    .rpc('get_players_from_user_clubs');

  if (error) {
    console.error('Error fetching players from user clubs:', error);
    throw error;
  }

  return data || [];
};

export const addClubMember = async (
  clubId: number, 
  playerId: number, 
  role: 'owner' | 'admin' | 'organizer' | 'member' = 'member'
): Promise<ClubMember> => {
  // First check if there's already an inactive membership record
  const { data: existingMember } = await supabase
    .from('club_members')
    .select('id')
    .eq('club_id', clubId)
    .eq('player_id', playerId)
    .eq('status', 'inactive')
    .single();

  if (existingMember) {
    // Reactivate the existing membership
    const { data, error } = await supabase
      .from('club_members')
      .update({
        status: 'active',
        role: role,
        joined_at: new Date().toISOString()
      })
      .eq('id', existingMember.id)
      .select()
      .single();

    if (error) {
      console.error('Error reactivating club member:', error);
      throw error;
    }

    return data;
  } else {
    // Create new membership record
    const { data, error } = await supabase
      .from('club_members')
      .insert({
        club_id: clubId,
        player_id: playerId,
        role: role,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding club member:', error);
      throw error;
    }

    return data;
  }
};

export const updateClubMember = async (
  membershipId: number, 
  updates: Partial<ClubMember>
): Promise<ClubMember> => {
  const { data, error } = await supabase
    .from('club_members')
    .update(updates)
    .eq('id', membershipId)
    .select()
    .single();

  if (error) {
    console.error('Error updating club member:', error);
    throw error;
  }

  return data;
};

export const removeClubMember = async (membershipId: number): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the membership details first to check permissions
  const { data: membership, error: fetchError } = await supabase
    .from('club_members')
    .select('club_id, player_id, role')
    .eq('id', membershipId)
    .single();

  if (fetchError || !membership) {
    throw new Error('Club membership not found');
  }

  // Get the current user's player ID
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer) {
    throw new Error('Player profile not found');
  }

  // Check if the current user is an admin of the club
  const { data: adminMembership } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', membership.club_id)
    .eq('player_id', currentPlayer.id)
    .eq('status', 'active')
    .single();

  if (!adminMembership || !['owner', 'admin'].includes(adminMembership.role)) {
    throw new Error('You do not have permission to remove members from this club');
  }

  // Don't allow removing the owner
  if (membership.role === 'owner') {
    throw new Error('Cannot remove the club owner');
  }

  // Delete the membership
  const { error } = await supabase
    .from('club_members')
    .delete()
    .eq('id', membershipId);

  if (error) {
    console.error('Error removing club member:', error);
    throw error;
  }

  // Also clean up any related invitation records for this player and club
  // This allows the player to be re-invited in the future
  const { error: invitationError } = await supabase
    .from('player_invitations')
    .delete()
    .eq('club_id', membership.club_id)
    .eq('invited_player_id', membership.player_id);

  if (invitationError) {
    console.error('Error cleaning up invitation records:', invitationError);
    // Don't throw here as the main operation (removing member) was successful
  }
};

export const leaveClub = async (clubId: number): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get player ID from user UUID
  const { data: player } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!player) {
    throw new Error('Player profile not found');
  }

  const { error } = await supabase
    .from('club_members')
    .update({ status: 'inactive' })
    .eq('club_id', clubId)
    .eq('player_id', player.id);

  if (error) {
    console.error('Error leaving club:', error);
    throw error;
  }
};

export const canPlayersPlay = async (
  playerAId: number, 
  playerBId: number, 
  clubId?: number
): Promise<boolean> => {
  const { data, error } = await supabase
    .rpc('can_players_play', { 
      p_player_a_id: playerAId, 
      p_player_b_id: playerBId, 
      p_club_id: clubId 
    });

  if (error) {
    console.error('Error checking if players can play:', error);
    return false;
  }

  return data || false;
};

// ==============================================
// CLUB INVITATION FUNCTIONS
// ==============================================

// Interface for player invitations
export interface PlayerInvitation {
  id: number;
  club_id: number;
  invited_player_id: number;
  invited_by_player_id: number;
  role: 'admin' | 'organizer' | 'member';
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  message?: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface PlayerInvitationDetails extends PlayerInvitation {
  club_name: string;
  club_description?: string;
  invited_player_name: string;
  invited_player_email: string;
  invited_by_name: string;
  invited_by_email: string;
}

// Create a player invitation
export const createPlayerInvitation = async (
  clubId: number,
  invitedPlayerId: number,
  role: 'admin' | 'organizer' | 'member' = 'member',
  message?: string
): Promise<PlayerInvitation> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the current user's player ID
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer) {
    throw new Error('Player profile not found');
  }

  // Check if the current user is an admin of the club
  const { data: membership } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', clubId)
    .eq('player_id', currentPlayer.id)
    .eq('status', 'active')
    .single();

  if (!membership || !['owner', 'admin'].includes(membership.role)) {
    throw new Error('You do not have permission to invite players to this club');
  }

  // Check if the player is already an active member
  const { data: existingMember } = await supabase
    .from('club_members')
    .select('id')
    .eq('club_id', clubId)
    .eq('player_id', invitedPlayerId)
    .eq('status', 'active')
    .single();

  if (existingMember) {
    throw new Error('Player is already a member of this club');
  }

  // Try to create the invitation directly
  // The unique constraint will prevent duplicates, and we'll handle that error
  const { data, error } = await supabase
    .from('player_invitations')
    .insert({
      club_id: clubId,
      invited_player_id: invitedPlayerId,
      invited_by_player_id: currentPlayer.id,
      role: role,
      message: message,
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days from now
    })
    .select()
    .single();

  if (error) {
    // Handle duplicate key constraint violation
    if (error.code === '23505' && error.message.includes('player_invitations_unique')) {
      // Check if there's an old invitation that should be cleaned up
      // This can happen if a player was removed but old invitation records remain
      const { data: oldInvitations } = await supabase
        .from('player_invitations')
        .select('id, status')
        .eq('club_id', clubId)
        .eq('invited_player_id', invitedPlayerId)
        .in('status', ['accepted', 'declined', 'expired']);

      if (oldInvitations && oldInvitations.length > 0) {
        // Clean up old invitations and try again
        await supabase
          .from('player_invitations')
          .delete()
          .eq('club_id', clubId)
          .eq('invited_player_id', invitedPlayerId)
          .in('status', ['accepted', 'declined', 'expired']);

        // Retry the invitation creation
        const { data: retryData, error: retryError } = await supabase
          .from('player_invitations')
          .insert({
            club_id: clubId,
            invited_player_id: invitedPlayerId,
            invited_by_player_id: currentPlayer.id,
            role: role,
            message: message,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          })
          .select()
          .single();

        if (retryError) {
          console.error('Error creating player invitation after cleanup:', retryError);
          throw retryError;
        }

        return retryData;
      }

      throw new Error('Player already has a pending invitation to this club');
    }
    
    // Only log error if it's not a handled duplicate key constraint
    console.error('Error creating player invitation:', error);
    throw error;
  }

  return data;
};

// Get invitations for a specific player
export const getPlayerInvitations = async (): Promise<PlayerInvitationDetails[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('player_invitation_details')
    .select('*')
    .eq('invited_player_id', (await getCurrentPlayerId())!)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching player invitations:', error);
    throw error;
  }

  return data || [];
};

// Get invitations for a specific club (for club management)
export const getClubInvitations = async (clubId: number): Promise<PlayerInvitationDetails[]> => {
  const { data, error } = await supabase
    .from('player_invitation_details')
    .select('*')
    .eq('club_id', clubId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching club invitations:', error);
    throw error;
  }

  return data || [];
};

// Accept an invitation
export const acceptInvitation = async (invitationId: number): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the invitation details
  const { data: invitation, error: fetchError } = await supabase
    .from('player_invitations')
    .select('*')
    .eq('id', invitationId)
    .single();

  if (fetchError || !invitation) {
    throw new Error('Invitation not found');
  }

  // Check if the current user is the invited player
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer || currentPlayer.id !== invitation.invited_player_id) {
    throw new Error('You are not authorized to accept this invitation');
  }

  // Check if invitation is still pending
  if (invitation.status !== 'pending') {
    throw new Error('This invitation is no longer pending');
  }

  // Check if invitation has expired
  if (new Date(invitation.expires_at) < new Date()) {
    throw new Error('This invitation has expired');
  }

  // Start a transaction to update invitation and add member
  const { error: updateError } = await supabase
    .from('player_invitations')
    .update({ 
      status: 'accepted',
      updated_at: new Date().toISOString()
    })
    .eq('id', invitationId);

  if (updateError) {
    console.error('Error updating invitation:', updateError);
    throw updateError;
  }

  // Add the player to the club (this will handle reactivating inactive memberships)
  try {
    await addClubMember(invitation.club_id, invitation.invited_player_id, invitation.role);
  } catch (memberError) {
    console.error('Error adding member to club:', memberError);
    throw memberError;
  }
};

// Decline an invitation
export const declineInvitation = async (invitationId: number): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the invitation details
  const { data: invitation, error: fetchError } = await supabase
    .from('player_invitations')
    .select('*')
    .eq('id', invitationId)
    .single();

  if (fetchError || !invitation) {
    throw new Error('Invitation not found');
  }

  // Check if the current user is the invited player
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer || currentPlayer.id !== invitation.invited_player_id) {
    throw new Error('You are not authorized to decline this invitation');
  }

  // Check if invitation is still pending
  if (invitation.status !== 'pending') {
    throw new Error('This invitation is no longer pending');
  }

  // Update the invitation status
  const { error } = await supabase
    .from('player_invitations')
    .update({ 
      status: 'declined',
      updated_at: new Date().toISOString()
    })
    .eq('id', invitationId);

  if (error) {
    console.error('Error declining invitation:', error);
    throw error;
  }
};

// Cancel an invitation (for club admins)
export const cancelInvitation = async (invitationId: number): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the invitation details
  const { data: invitation, error: fetchError } = await supabase
    .from('player_invitations')
    .select('*')
    .eq('id', invitationId)
    .single();

  if (fetchError || !invitation) {
    throw new Error('Invitation not found');
  }

  // Check if the current user is an admin of the club
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer) {
    throw new Error('Player profile not found');
  }

  const { data: membership } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', invitation.club_id)
    .eq('player_id', currentPlayer.id)
    .eq('status', 'active')
    .single();

  if (!membership || !['owner', 'admin'].includes(membership.role)) {
    throw new Error('You do not have permission to cancel this invitation');
  }

  // Delete the invitation
  const { error } = await supabase
    .from('player_invitations')
    .delete()
    .eq('id', invitationId);

  if (error) {
    console.error('Error canceling invitation:', error);
    throw error;
  }
};

// Interface for player search results
interface PlayerSearchResult {
  id: number;
  preName: string;
  name: string;
  email: string;
  pin: string;
}

// Search for players by name or email
export const searchPlayers = async (query: string): Promise<PlayerSearchResult[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Check if the current user is a global admin
  const { data: adminCheck } = await supabase
    .from('user_licenses')
    .select('global_admin')
    .eq('user_id', user.id)
    .eq('status', 'active')
    .single();

  const isGlobalAdmin = adminCheck?.global_admin === true;

  // Build the query - exclude incomplete profiles for non-global admins
  let queryBuilder = supabase
    .from('players')
    .select('id, "preName", name, email, pin')
    .or(`"preName".ilike.%${query}%,name.ilike.%${query}%,email.ilike.%${query}%`)
    .is('deleted_at', null);

  // For non-global admins, only show players with complete profiles
  if (!isGlobalAdmin) {
    queryBuilder = queryBuilder
      .not('name', 'is', null)
      .not('"preName"', 'is', null)
      .not('pin', 'is', null)
      .neq('name', '')
      .neq('"preName"', '')
      .neq('pin', '');
  }

  const { data, error } = await queryBuilder.limit(10);

  if (error) {
    console.error('Error searching players:', error);
    throw error;
  }

  return data || [];
};

// Invite a player to a club
export const invitePlayerToClub = async (
  clubId: number, 
  playerId: number, 
  role: 'admin' | 'member' = 'member'
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the current user's player ID
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer) {
    throw new Error('Player profile not found');
  }

  // Check if the current user is an admin of the club
  const { data: membership } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', clubId)
    .eq('player_id', currentPlayer.id)
    .eq('status', 'active')
    .single();

  if (!membership || !['owner', 'admin'].includes(membership.role)) {
    throw new Error('You do not have permission to invite players to this club');
  }

  // Check if the player is already a member
  const { data: existingMember } = await supabase
    .from('club_members')
    .select('id')
    .eq('club_id', clubId)
    .eq('player_id', playerId)
    .single();

  if (existingMember) {
    throw new Error('Player is already a member of this club');
  }

  // Add the player as a member
  const { error } = await supabase
    .from('club_members')
    .insert({
      club_id: clubId,
      player_id: playerId,
      role: role,
      invited_by: currentPlayer.id,
      status: 'active'
    });

  if (error) {
    console.error('Error inviting player to club:', error);
    throw error;
  }
};

// Remove a player from a club
export const removePlayerFromClub = async (
  clubId: number, 
  playerId: number
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get the current user's player ID
  const { data: currentPlayer } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (!currentPlayer) {
    throw new Error('Player profile not found');
  }

  // Check if the current user is an admin of the club
  const { data: membership } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', clubId)
    .eq('player_id', currentPlayer.id)
    .eq('status', 'active')
    .single();

  if (!membership || !['owner', 'admin'].includes(membership.role)) {
    throw new Error('You do not have permission to remove players from this club');
  }

  // Don't allow removing the owner
  const { data: targetMember } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', clubId)
    .eq('player_id', playerId)
    .eq('status', 'active')
    .single();

  if (targetMember?.role === 'owner') {
    throw new Error('Cannot remove the club owner');
  }

  // Remove the player from the club
  const { error } = await supabase
    .from('club_members')
    .delete()
    .eq('club_id', clubId)
    .eq('player_id', playerId);

  if (error) {
    console.error('Error removing player from club:', error);
    throw error;
  }
};

// Helper function to get current user's player ID
export const getCurrentPlayerId = async (): Promise<number | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return null;
  }

  const { data: player } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  return player?.id || null;
};

// ==============================================
// ORGANIZER PERMISSION FUNCTIONS
// ==============================================

/**
 * Check if a player has organizer-level permissions in a club
 * Organizers can create tournaments and events
 */
export const canCreateEvents = async (clubId: number, playerId?: number): Promise<boolean> => {
  try {
    const currentPlayerId = playerId || await getCurrentPlayerId();
    if (!currentPlayerId) return false;

    const { data: membership } = await supabase
      .from('club_members')
      .select('role')
      .eq('club_id', clubId)
      .eq('player_id', currentPlayerId)
      .eq('status', 'active')
      .single();

    return membership?.role && ['owner', 'admin', 'organizer'].includes(membership.role);
  } catch (error) {
    console.error('Error checking organizer permissions:', error);
    return false;
  }
};

/**
 * Check if a player can manage club members (admin level)
 */
export const canManageMembers = async (clubId: number, playerId?: number): Promise<boolean> => {
  try {
    const currentPlayerId = playerId || await getCurrentPlayerId();
    if (!currentPlayerId) return false;

    const { data: membership } = await supabase
      .from('club_members')
      .select('role')
      .eq('club_id', clubId)
      .eq('player_id', currentPlayerId)
      .eq('status', 'active')
      .single();

    return membership?.role && ['owner', 'admin'].includes(membership.role);
  } catch (error) {
    console.error('Error checking admin permissions:', error);
    return false;
  }
};

/**
 * Check if a player can delete a club (owner only)
 */
export const canDeleteClub = async (clubId: number, playerId?: number): Promise<boolean> => {
  try {
    const currentPlayerId = playerId || await getCurrentPlayerId();
    if (!currentPlayerId) return false;

    const { data: membership } = await supabase
      .from('club_members')
      .select('role')
      .eq('club_id', clubId)
      .eq('player_id', currentPlayerId)
      .eq('status', 'active')
      .single();

    return membership?.role === 'owner';
  } catch (error) {
    console.error('Error checking owner permissions:', error);
    return false;
  }
};

/**
 * Get the role level hierarchy for display purposes
 */
export const getRoleLevel = (role: string): number => {
  switch (role) {
    case 'owner': return 4;
    case 'admin': return 3;
    case 'organizer': return 2;
    case 'member': return 1;
    default: return 0;
  }
};

/**
 * Check if one role can manage another role
 */
export const canManageRole = (managerRole: string, targetRole: string): boolean => {
  const managerLevel = getRoleLevel(managerRole);
  const targetLevel = getRoleLevel(targetRole);
  return managerLevel > targetLevel;
};
